import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Info, Sparkles } from "lucide-react";
import { openAiChat } from "@/components/ai/AiChat";
import { motion, useReducedMotion } from "framer-motion";
import { LogoFull } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { NutritionTable } from "@/components/nutrition/NutritionTable";
import { MealPrep } from "@/components/nutrition/MealPrep";
import { RecipeComparison } from "@/components/nutrition/RecipeComparison";
import { AdditionsAndTips } from "@/components/nutrition/AdditionsAndTips";

export default function NutritionGuide() {
  const reducedMotion = useReducedMotion();
  useEffect(() => {
    const previousTitle = document.title;
    document.title = "Millet Nutrition & Meal Prep | Kandula Home Foods";
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
    return () => { document.title = previousTitle; };
  }, []);

  return (
    <div className="min-h-screen bg-cream font-body text-ragi-dark" data-testid="nutrition-guide-page">
      <header className="border-b border-cream-muted">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-4 py-5 sm:px-8">
          <Link to="/" aria-label="Kandula Home Foods home" data-testid="nutrition-brand-link"><LogoFull /></Link>
          <Button asChild variant="ghost" className="text-ragi-dark"><Link to="/#catalog" data-testid="nutrition-back-to-shop"><ArrowLeft />Back to shop</Link></Button>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-4 sm:px-8">
        <motion.div initial={reducedMotion ? false : { opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="pt-12 sm:pt-16">
          <p className="mb-4 font-mono text-xs uppercase text-terracotta" data-testid="nutrition-product-name">Sprouted 7-Millet Protein Powder</p>
          <h1 className="max-w-3xl font-display text-4xl font-semibold leading-tight sm:text-5xl lg:text-6xl" data-testid="nutrition-guide-title">Know your portion.<br /><span className="italic text-terracotta">Make it your own.</span></h1>
          <p className="mt-5 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base" data-testid="nutrition-guide-description">Nutrition & meal prep, from a spoonful of dry millet powder to your everyday bowl.</p>
          <button onClick={() => openAiChat("How should I use the Sprouted 7-Millet Protein Powder for my daily protein needs? Give me a simple plan.")} className="mt-6 inline-flex items-center gap-2 rounded-full bg-ragi-dark px-5 py-3 text-sm font-semibold text-cream transition-transform hover:scale-[1.03]" data-testid="nutrition-ask-ai-button"><Sparkles className="h-4 w-4 text-saffron" /> Ask the AI nutritionist</button>
          <aside className="mt-8 flex max-w-3xl items-start gap-3 border-l-2 border-saffron bg-saffron-subtle px-4 py-4" data-testid="nutrition-estimate-disclaimer">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-terracotta" aria-hidden="true" />
            <p className="text-xs leading-relaxed">Planning estimates supplied by Kandula Home Foods, <strong>not lab-verified nutrition facts</strong>. Values vary with the grain blend and preparation. Raw dry powder only, unless additions are listed. Not a substitute for personalised dietary advice.</p>
          </aside>
        </motion.div>
        <nav className="mt-9 flex flex-wrap gap-x-6 gap-y-3 border-b border-cream-muted pb-5 text-sm" aria-label="Nutrition guide sections" data-testid="nutrition-section-nav">
          {[["nutrition-values", "Nutrition"], ["meal-prep", "Meal prep & macros"], ["bowl-recipes", "Bowl recipes"], ["everyday-tips", "Everyday tips"]].map(([id, label]) => (
            <a key={id} href={`#${id}`} className="underline-offset-4 transition-colors hover:text-terracotta hover:underline focus-visible:underline" data-testid={`nutrition-nav-${id}`}>{label}</a>
          ))}
        </nav>
        <NutritionTable />
        <MealPrep />
        <RecipeComparison />
        <AdditionsAndTips />
      </main>
      <footer className="bg-ragi-dark text-cream">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-5 px-4 py-9 sm:px-8">
          <p className="font-display text-lg" data-testid="nutrition-footer-brand">Made with Love by Kandulas.</p>
          <Link to="/#catalog" className="inline-flex items-center gap-2 text-sm underline-offset-4 transition-colors hover:text-saffron hover:underline" data-testid="nutrition-footer-shop-link">Back to the heritage shop<ArrowUpRight className="h-4 w-4" /></Link>
        </div>
      </footer>
    </div>
  );
}